"use client";

import { useEffect, useState } from "react";
import { useAppSelector } from "../../redux/hooks";
import { syncCardVisible } from "../../lib/syncPolicy.js";
import { Button } from "../ui/button.jsx";
import { Download, X } from "lucide-react";

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true);

/**
 * Phase 3 — install prompt.
 *
 * Chromium (Chrome/Edge on Android) fires `beforeinstallprompt` when the
 * PWA criteria are met (manifest + registered service worker). We capture
 * that event, suppress the browser's default mini-infobar, and show our
 * own banner so auditors are guided to install the app to their home
 * screen for reliable offline access.
 */
export function InstallPrompt() {
  const showSyncCard = useAppSelector(syncCardVisible);
  const [deferredEvent, setDeferredEvent] = useState<any>(null);
  const [installed, setInstalled] = useState(isStandalone);
  const [dismissed, setDismissed] = useState(
    () =>
      typeof window !== "undefined" &&
      sessionStorage.getItem("encoaudit-install-dismissed") === "1",
  );

  useEffect(() => {
    const standalone = window.matchMedia?.("(display-mode: standalone)");
    let installedInSession = isStandalone();
    const handleInstalled = () => {
      installedInSession = true;
      setInstalled(true);
      setDeferredEvent(null);
    };
    const handler = (event: any) => {
      event.preventDefault();
      if (!installedInSession) setDeferredEvent(event);
    };
    const handleDisplayModeChange = () => {
      if (standalone?.matches) handleInstalled();
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", handleInstalled);
    standalone?.addEventListener?.("change", handleDisplayModeChange);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", handleInstalled);
      standalone?.removeEventListener?.("change", handleDisplayModeChange);
    };
  }, []);

  if (!deferredEvent || installed || dismissed || showSyncCard) return null;

  const handleInstall = async () => {
    deferredEvent.prompt();
    const { outcome } = await deferredEvent.userChoice;
    console.log("[v0] Install prompt outcome:", outcome);
    setDeferredEvent(null);
  };

  const handleDismiss = () => {
    sessionStorage.setItem("encoaudit-install-dismissed", "1");
    setDismissed(true);
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 mx-auto flex max-w-md items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 shadow-lg sm:left-auto sm:right-4">
      <Download className="w-5 h-5 shrink-0 text-primary dark:text-link" />
      <div className="flex-1 text-sm text-foreground">
        Install EncoAudit for offline access at audit sites.
      </div>
      <Button type="button" size="sm" onClick={handleInstall}>
        Install
      </Button>
      <button
        type="button"
        aria-label="Dismiss install prompt"
        className="text-muted-foreground hover:text-foreground"
        onClick={handleDismiss}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
